import React, { useEffect, useState } from 'react';
import RepresentativeLayout from '../../components/layout/RepresentativeLayout';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Users, Search, Eye } from 'lucide-react';
import { representativeAPI } from '../../services/api';
import { toast } from 'sonner';

export default function RepStudents() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchStudents();
  }, [page]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await representativeAPI.getStudents({ page, limit: 20 });
      setStudents(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      inactive: 'bg-gray-100 text-gray-700',
      completed: 'bg-blue-100 text-blue-700'
    };
    return <Badge className={styles[status] || 'bg-gray-100 text-gray-700'}>{status}</Badge>;
  };

  const filteredStudents = students.filter(s => {
    if (!search) return true;
    const name = `${s.user?.firstName || ''} ${s.user?.lastName || ''}`.toLowerCase();
    return name.includes(search.toLowerCase()) || (s.user?.email || '').toLowerCase().includes(search.toLowerCase());
  });

  return (
    <RepresentativeLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">My Students</h1>
            <p className="mt-1 text-slate-600">Students you have referred to Fly8</p>
          </div>
          <Badge variant="outline" className="border-[#A87DFA]/25 text-[#6D49D4]">
            {pagination.total} Total
          </Badge>
        </div>

        {/* Search */}
        <div className="partner-card rounded-[24px] p-3">
          <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8D68E8]" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-2xl border border-[#A87DFA]/18 bg-white/70 py-3 pl-10 pr-4 text-sm"
          />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredStudents.length === 0 ? (
          <Card className="partner-card rounded-[28px] p-12 text-center">
            <Users className="mx-auto mb-3 h-12 w-12 text-[#A87DFA]" />
            <h3 className="text-lg font-semibold text-slate-900">No students found</h3>
            <p className="mt-1 text-sm text-slate-600">Students you refer will appear here</p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredStudents.map((student) => (
              <Card key={student.studentId} className="partner-card partner-card-hover rounded-[26px] p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-400 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {student.user?.firstName?.[0]}{student.user?.lastName?.[0]}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {student.user?.firstName} {student.user?.lastName}
                      </p>
                      <p className="text-sm text-slate-500">{student.user?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(student.status)}
                    {student.country && (
                      <span className="text-xs text-slate-500">{student.country}</span>
                    )}
                    <Eye className="w-4 h-4 text-[#7A55DF]" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="partner-secondary-btn rounded-2xl px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-slate-600">
              Page {page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
              disabled={page === pagination.totalPages}
              className="partner-secondary-btn rounded-2xl px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </RepresentativeLayout>
  );
}
